import { Observable, catchError, map, of } from 'rxjs'
import { Injectable } from '@angular/core'

import { IPaymentCorrection, IPaymentCorrectionResult, IPaymentCreateResult, IPaymentList } from '@modules/private/payment/data/interfaces/ipayment.interface'
import { LeaseLedger } from '@modules/private/lease/data/interfaces/lease-ledger.interface'
import { IResponseArray } from '@data/interfaces/iresponseArray.interface'
import { FilterOptions } from '@shared/interfaces/filterOptions.interface'
import { ApiClass } from '@data/class/api-class'
import { environment } from '@env/environment'

@Injectable({
  providedIn: 'root',
})
export class ApiPaymentService extends ApiClass {
  constructor() {
    super()
    this.path = 'payments'
  }

  getAll(page = 1, limit = 10, filters = {}) {
    this.path = 'payments'
    const response: IPaymentList = { error: true, msg: '', data: [], total: 0, status: 200, page: 1, itemsByPage: 10, totalItems: 0 }
    const populate = [{ path: 'account' }, { path: 'user', select: ['names'] }, { path: 'client', select: ['name'] }]

    return this.http.post<IResponseArray>(environment.apiUrl + this.path, { page, limit, filters, populate }).pipe(
      map((r) => {
        if (!r || !r.data.data || r.data.data.length === 0) return response
        return r as IPaymentList
      }),
      catchError(() => of(response)),
    )
  }

  /**
   * Registra un pago conservando el 409 de posible duplicado (F16).
   *
   * No usa `postCollection`: el `catchError` de `ApiClass` solo copia `error`, `msg` y `data`, así que
   * `code` y `detail` —los gemelos y cuántos arrendamientos activos tiene el cliente— se perderían y el
   * diálogo no tendría con qué pintar la evidencia. Tocar `ApiClass` afectaría a todos los servicios.
   */
  createWithDuplicateCheck(data: any): Observable<IPaymentCreateResult> {
    return this.http.post(environment.apiUrl + 'payments/create', data).pipe(
      map((r) => r as IPaymentCreateResult),
      catchError((e) =>
        of({
          error: true,
          msg: e?.error?.msg || 'No se pudo registrar el pago',
          data: e?.error?.data ?? null,
          code: e?.error?.code,
          detail: e?.error?.detail,
        }),
      ),
    )
  }

  fetchAccounts(filterOptions: FilterOptions) {
    this.path = 'accounts'

    return this.postCollection(filterOptions).pipe(
      map((response) => {
        if (!response.error && Array.isArray(response.data.data)) {
          return response.data.data.map((account: { _id: string; alias: string }) => ({
            label: account.alias,
            value: account._id,
          }))
        } else {
          console.error('Error en la respuesta o los datos no son un array:', response.msg)
          return []
        }
      }),
      catchError((error) => {
        console.error('Error al obtener las cuentas:', error)
        return []
      }),
    )
  }

  getClients() {
    this.path = 'clients'
    const filter: FilterOptions = {
      page: 1,
      limit: 1000,
      filters: {},
      select: ['name', '_id'],
    }

    return this.postCollection(filter).pipe(map((r) => r as { error: boolean; msg: string; data: any }))
  }

  /**
   * Pagos que necesitan atención: los que arrastran un remanente de centavos que impide cerrarlos,
   * los que tienen un gemelo dentro de la ventana de duplicados, y los que ya se anularon desde la
   * herramienta —que están para poder deshacerlo—. Requiere el permiso `payments.adjust`.
   */
  getNeedingAttention(threshold?: number) {
    const query = threshold != null ? `?threshold=${threshold}` : ''

    return this.http.get(environment.apiUrl + `payments/needing-attention${query}`).pipe(
      map((r) => r as { error: boolean; msg: string; data: any }),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudieron cargar los pagos por atender', data: null })),
    )
  }

  /**
   * Estado del pago, sus aplicaciones vivas y su bitácora: lo que el modal necesita para poder
   * decirle al operador qué le va a pasar al dinero. Funciona también sobre pagos anulados —que son
   * justo los que hay que poder revisar cuando algo salió mal—, porque el backend los lee sin pasar
   * por Mongoose.
   *
   * También lo pide la fila desplegada de Cobranza › Dinero para «A dónde fue el dinero». Requiere el
   * permiso `payments.view` (no `payments.adjust`): Contabilidad despliega la fila sin poder ajustar.
   */
  getPaymentContext(paymentId: string) {
    return this.http.get(environment.apiUrl + `payments/${paymentId}/context`).pipe(
      map((r) => r as { error: boolean; msg: string; data: any }),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudo cargar la información del pago', data: null })),
    )
  }

  /**
   * Ajusta un pago. Con `dryRun` el backend calcula todo y aborta la transacción: la
   * previsualización corre el mismo código que la aplicación, así que no puede desincronizarse.
   * Requiere el permiso `payments.adjust`.
   */
  adjustPayment(paymentId: string, payload: any, dryRun: boolean) {
    return this.http.post(environment.apiUrl + `payments/${paymentId}/adjust?dryRun=${dryRun}`, payload).pipe(
      map((r) => r as { error: boolean; msg: string; data: any }),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudo procesar el ajuste del pago', data: null })),
    )
  }

  /**
   * Corrige la fecha de un pago a la del depósito real. El motivo es obligatorio: el backend sella
   * la fecha anterior y la nueva en la bitácora del pago y en la de cada arrendamiento donde está
   * aplicado. Requiere el permiso `payments.adjust`.
   */
  changePaymentDate(paymentId: string, date: string, reason: string) {
    return this.http.patch(environment.apiUrl + `payments/${paymentId}/date`, { date, reason }).pipe(
      map((r) => r as { error: boolean; msg: string; data: any }),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudo corregir la fecha del pago', data: null })),
    )
  }

  /**
   * Corrige el monto o la clave de rastreo de un pago (F-045). Requiere `payments.adjust`.
   *
   * Conserva el sobre completo del error por la misma razón que `createWithDuplicateCheck`: el
   * `catchError` de `ApiClass` perdería `code` y `detail`, y sin ellos el diálogo no puede distinguir
   * un gemelo (409 `POSSIBLE_DUPLICATE`, con evidencia) de una carrera (409 `PAYMENT_CHANGED`) ni de
   * un monto bloqueado por sus aplicaciones (400 `PAYMENT_HAS_APPLICATIONS`).
   */
  correctPayment(paymentId: string, dto: IPaymentCorrection): Observable<IPaymentCorrectionResult> {
    return this.http.patch(environment.apiUrl + `payments/${paymentId}/correction`, dto).pipe(
      map((r) => r as IPaymentCorrectionResult),
      catchError((e) =>
        of({
          error: true,
          msg: e?.error?.msg || 'No se pudo corregir el pago',
          data: e?.error?.data ?? null,
          code: e?.error?.code,
          detail: e?.error?.detail,
        }),
      ),
    )
  }

  /**
   * Libro mayor de un arrendamiento en una sola llamada (PRD Exigibilidad §7.1). Compuerta
   * `payments.get-all` + ownership por el cliente del lease, igual que `by-lease`. No usa
   * `this.path` a propósito: `create`/`fetchAccounts`/`getClients` lo mutan.
   */
  getLedger(leaseId: string): Observable<{ error: boolean; msg: string; data: LeaseLedger | null }> {
    return this.http.get(environment.apiUrl + `payments/ledger/${leaseId}`).pipe(
      map((r) => r as { error: boolean; msg: string; data: LeaseLedger }),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudo cargar el libro mayor del arrendamiento', data: null })),
    )
  }
}
